import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, FileText, CheckCircle, AlertCircle, Trash2 } from "lucide-react";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";

interface LivingstonCountyTextParserProps {
  onImportComplete?: () => void;
}

interface ParsedRecord {
  filingDate: string;
  documentNumber: string;
  documentType: string;
  fromParty: string;
  toParty: string;
  ppn: string;
}

interface ImportResult {
  totalParsed: number;
  duplicatesSkipped: number;
  familyFiltered: number;
  businessFiltered: number;
  bankFiltered: number;
  existingSkipped: number;
  imported: number;
}

const BANK_KEYWORDS = [
  'BANK', 'MORTGAGE', 'CREDIT UNION', 'FEDERAL SAVINGS', 'LOAN',
  'FINANCIAL', 'LENDING', 'CAPITAL', 'FUNDING', 'FANNIE', 'FREDDIE',
  'HUD', 'VETERANS', 'FHA', 'RURAL DEVELOPMENT', 'NATIONSTAR',
  'QUICKEN', 'ROCKET', 'WELLS FARGO', 'CHASE', 'CITIMORTGAGE'
];

const BUSINESS_KEYWORDS = [
  'LLC', 'INC', 'CORP', 'COMPANY', 'LTD', 'HOLDINGS', 'PROPERTIES',
  'ENTERPRISES', 'ASSOCIATES', 'GROUP', 'PARTNERS', 'DEVELOPMENT',
  'INVESTMENTS', 'REALTY', 'REAL ESTATE', 'CONSTRUCTION', 'BUILDERS',
  'SERVICES', 'MANAGEMENT', 'SOLUTIONS'
];

const TRUST_KEYWORDS = ['TRUST', 'TRUSTEE', 'REVOCABLE', 'IRREVOCABLE', 'LIVING TRUST'];

const extractLastName = (name: string): string => {
  const cleaned = name.trim().toUpperCase();
  // For "LAST, FIRST" format
  if (cleaned.includes(',')) {
    return cleaned.split(',')[0].trim().split(/\s+/)[0];
  }
  // For "FIRST LAST" format, last word is typically last name
  const parts = cleaned.split(/\s+/);
  return parts[parts.length - 1];
};

const isBusinessEntity = (name: string): boolean => {
  const upper = name.toUpperCase();
  return BUSINESS_KEYWORDS.some(kw => upper.includes(kw)) ||
         TRUST_KEYWORDS.some(kw => upper.includes(kw));
};

const isBankEntity = (name: string): boolean => {
  const upper = name.toUpperCase();
  return BANK_KEYWORDS.some(kw => upper.includes(kw));
};

const isFamilyTransfer = (fromParty: string, toParty: string): boolean => {
  // Skip if either party is a business/trust
  if (isBusinessEntity(fromParty) || isBusinessEntity(toParty)) return false;
  if (isBankEntity(fromParty) || isBankEntity(toParty)) return false;

  const fromLast = extractLastName(fromParty);
  const toLast = extractLastName(toParty);

  // Handle "&" joined names (co-sellers/buyers)
  // e.g. "SMITH, JOHN & SMITH, JANE" - extract first last name
  if (fromLast === toLast && fromLast.length > 1) {
    return true;
  }
  return false;
};

const isBusinessToBusinessTransfer = (fromParty: string, toParty: string): boolean => {
  const fromBiz = isBusinessEntity(fromParty) || isBankEntity(fromParty);
  const toBiz = isBusinessEntity(toParty) || isBankEntity(toParty);
  return fromBiz && toBiz;
};

export function LivingstonCountyTextParser({ onImportComplete }: LivingstonCountyTextParserProps) {
  const [rawText, setRawText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const { toast } = useToast();

  const parseDate = (dateStr: string): string => {
    // Format: MM/DD/YYYY -> YYYY-MM-DD
    const parts = dateStr.trim().split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  };

  const parseLivingstonText = (text: string): ParsedRecord[] => {
    const records: ParsedRecord[] = [];
    const lines = text.split('\n');

    // Known doc type tokens, ordered longest-first to match greedily
    const DOC_TYPE_TOKENS = [
      'MEMORANDUM OF LAND CONTRACT', 'MEMO OF LAND CONTRACT', 'MEMO LAND CONTRACT',
      'LAND CONTRACT', 'DIS ASGN LC', 'DIS ASGN', 'DISCHARGE LC', 'EXTEND LC',
      'EXTENSION LC', 'AMEND LC', 'AMENDMENT LC', 'ASGN LC', 'ASSIGNMENT LC',
      'MEMO LC', 'MLC', 'DIS LC', 'LC'
    ];

    const LC_ACTIVE_TYPES = [
      'LC', 'MLC', 'LAND CONTRACT', 'MEMORANDUM OF LAND CONTRACT',
      'MEMO LAND CONTRACT', 'MEMO OF LAND CONTRACT', 'MEMO LC',
      'ASGN LC', 'ASSIGNMENT LC', 'EXTEND LC', 'EXTENSION LC',
      'AMEND LC', 'AMENDMENT LC'
    ];

    const INACTIVE_KEYWORDS = ['DIS LC', 'DIS ASGN', 'DISCHARGE', 'RELEASE', 'CANCEL'];

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      // Skip non-data lines
      if (trimmed === 'Add to Cart' || 
          trimmed.startsWith('Check for Related') ||
          trimmed.startsWith('From Party') || 
          trimmed.startsWith('---') || 
          trimmed.includes('DocType')) continue;

      // Find date pattern MM/DD/YYYY (possibly followed by time)
      const dateMatch = trimmed.match(/(\d{1,2}\/\d{1,2}\/\d{4})/);
      if (!dateMatch) continue;

      // Find the full date+time string to determine split point
      const fullDateTimeMatch = trimmed.match(/\d{1,2}\/\d{1,2}\/\d{4}(\s+\d{1,2}:\d{2}:\d{2}\s*[AP]M)?/i);
      if (!fullDateTimeMatch) continue;

      const dateTimeStr = fullDateTimeMatch[0];
      const dateTimeIdx = trimmed.indexOf(dateTimeStr);
      const beforeDate = trimmed.substring(0, dateTimeIdx).trim();
      const afterDateTime = trimmed.substring(dateTimeIdx + dateTimeStr.length).trim();

      // After date/time: "/ DOCNUMBER PPN" or just "DOCNUMBER PPN"
      const afterCleaned = afterDateTime.replace(/^\s*\/\s*/, '').trim();
      const afterParts = afterCleaned.split(/\s{2,}/).map(p => p.trim()).filter(p => p);
      const documentNumber = afterParts[0] || '';
      const ppn = afterParts[1] || '';

      if (!documentNumber) continue;

      // Extract doc type by matching known tokens at the END of beforeDate
      const beforeUpper = beforeDate.toUpperCase();
      let docType = '';
      let beforeParties = beforeDate;

      for (const token of DOC_TYPE_TOKENS) {
        if (beforeUpper.endsWith(token)) {
          docType = token;
          // Remove the doc type from the end to get just the party names
          beforeParties = beforeDate.substring(0, beforeDate.length - token.length).trim();
          break;
        }
      }

      if (!docType) continue;

      const docTypeUpper = docType.toUpperCase();

      // Check if it's a land-contract-related doc type
      const isLandContract = LC_ACTIVE_TYPES.some(t => docTypeUpper === t) || 
                             docTypeUpper.includes('LAND CONTRACT');
      // Also allow inactive types through the type check (we filter them next)
      const isAnyLC = isLandContract || INACTIVE_KEYWORDS.some(t => docTypeUpper.includes(t));
      if (!isAnyLC) continue;

      // Skip discharge/release/inactive types
      const isInactive = INACTIVE_KEYWORDS.some(t => docTypeUpper.includes(t));
      if (isInactive) continue;

      // Split parties by 2+ spaces
      const partyParts = beforeParties.split(/\s{2,}/).map(p => p.trim()).filter(p => p);

      let fromParty = '';
      let toParty = '';

      if (partyParts.length >= 2) {
        fromParty = partyParts[0];
        toParty = partyParts.slice(1).join(' ');
      } else if (partyParts.length === 1) {
        // Might be tab-separated
        const tabParts = partyParts[0].split('\t').map(p => p.trim()).filter(p => p);
        if (tabParts.length >= 2) {
          fromParty = tabParts[0];
          toParty = tabParts.slice(1).join(' ');
        } else {
          continue;
        }
      } else {
        continue;
      }

      if (!fromParty || !toParty) continue;

      records.push({
        filingDate: parseDate(dateMatch[1]),
        documentNumber,
        documentType: docType,
        fromParty,
        toParty,
        ppn
      });
    }

    console.log('Total Livingston records parsed:', records.length);
    return records;
  };

  const processRecords = async () => {
    if (!rawText.trim()) {
      toast({ title: "No data", description: "Please paste some data to import", variant: "destructive" });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      const allRecords = parseLivingstonText(rawText);
      setProgress(20);

      const result: ImportResult = {
        totalParsed: allRecords.length,
        duplicatesSkipped: 0,
        familyFiltered: 0,
        businessFiltered: 0,
        bankFiltered: 0,
        existingSkipped: 0,
        imported: 0
      };

      // Deduplicate by document number
      const uniqueRecords = new Map<string, ParsedRecord>();
      for (const record of allRecords) {
        if (!uniqueRecords.has(record.documentNumber)) {
          uniqueRecords.set(record.documentNumber, record);
        } else {
          result.duplicatesSkipped++;
        }
      }
      setProgress(30);

      // Apply filters
      const validRecords: ParsedRecord[] = [];
      for (const record of uniqueRecords.values()) {
        // Filter bank transactions
        if (isBankEntity(record.fromParty) || isBankEntity(record.toParty)) {
          result.bankFiltered++;
          continue;
        }
        // Filter business-to-business
        if (isBusinessToBusinessTransfer(record.fromParty, record.toParty)) {
          result.businessFiltered++;
          continue;
        }
        // Filter family-to-family
        if (isFamilyTransfer(record.fromParty, record.toParty)) {
          result.familyFiltered++;
          continue;
        }
        validRecords.push(record);
      }
      setProgress(50);

      // Check existing in database
      const docNumbers = validRecords.map(r => r.documentNumber);
      if (docNumbers.length > 0) {
        const { data: existing } = await supabase
          .from('county_research')
          .select('document_number')
          .eq('county', 'livingston')
          .in('document_number', docNumbers);

        const existingSet = new Set(existing?.map(e => e.document_number) || []);
        setProgress(60);

        const recordsToInsert = validRecords.filter(r => {
          if (existingSet.has(r.documentNumber)) {
            result.existingSkipped++;
            return false;
          }
          return true;
        });

        if (recordsToInsert.length > 0) {
          const insertData = recordsToInsert.map(record => ({
            county: 'livingston',
            document_number: record.documentNumber,
            document_type: record.documentType,
            filing_date: record.filingDate,
            grantor_name: record.fromParty,
            grantee_name: record.toParty,
            notes: record.ppn ? `PPN: ${record.ppn}` : null,
            is_potential_owner_finance: true,
            confidence_score: 80,
            detection_reasons: JSON.stringify(['Land Contract document type', 'Fidlar/Tapestry platform']),
            search_date: new Date().toISOString()
          }));

          const batchSize = 50;
          for (let i = 0; i < insertData.length; i += batchSize) {
            const batch = insertData.slice(i, i + batchSize);
            const { error } = await supabase.from('county_research').insert(batch);
            if (error) {
              console.error('Insert error:', error);
              throw error;
            }
            result.imported += batch.length;
            setProgress(60 + ((i + batch.length) / insertData.length) * 35);
          }
          backupRecordsToExcel(insertData, 'livingston', 'livingston_parser');
        }
      }

      setProgress(100);
      setImportResult(result);
      setRawText(""); // Auto-clear after processing

      toast({
        title: "Import complete",
        description: `Imported ${result.imported} records from Livingston County`
      });

      if (onImportComplete) onImportComplete();
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Import failed",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setRawText(text);
    toast({ title: "File loaded", description: `Loaded ${file.name} - ${text.split('\n').length} lines` });
  };

  return (
    <div className="space-y-4">
      {importResult && (
        <Alert className={importResult.imported > 0 ? "border-green-500" : "border-yellow-500"}>
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Import Results</AlertTitle>
          <AlertDescription>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              <div>Total Records Parsed: <strong>{importResult.totalParsed}</strong></div>
              <div>Duplicates Skipped: <strong>{importResult.duplicatesSkipped}</strong></div>
              <div>Family Transfers Filtered: <strong>{importResult.familyFiltered}</strong></div>
              <div>Business-to-Business Filtered: <strong>{importResult.businessFiltered}</strong></div>
              <div>Bank Transactions Filtered: <strong>{importResult.bankFiltered}</strong></div>
              <div>Already in Database: <strong>{importResult.existingSkipped}</strong></div>
              <div className="text-green-600">Successfully Imported: <strong>{importResult.imported}</strong></div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Livingston County, MI - Land Contract Parser
          </CardTitle>
          <CardDescription>
            Paste search results from Livingston County Register of Deeds (Fidlar/Tapestry platform).
            Automatically filters out family-to-family transfers, business-to-business transactions, 
            and bank/institutional deals.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <label className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Upload Text File
                <input type="file" accept=".txt" onChange={handleFileUpload} className="hidden" />
              </label>
            </Button>
            <Button variant="outline" onClick={() => setRawText("")} disabled={!rawText}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>

          <Textarea
            placeholder={`Paste Livingston County search results here...

Expected format (tab or space separated):
From Party          To Party          DocType    Record Date    Document #    PPN

Example:
SMITH, JOHN         DOE, JANE         LC         01/15/2024     2024-001234   L1234

Records with these doc types are imported: LC, MLC, LAND CONTRACT
Records with DIS LC, DISCHARGE, RELEASE are skipped (inactive contracts)`}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            className="min-h-[300px] font-mono text-xs"
          />

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">
                Processing... {Math.round(progress)}%
              </p>
            </div>
          )}

          <Button onClick={processRecords} disabled={isProcessing || !rawText.trim()} className="w-full">
            {isProcessing ? "Processing..." : "Parse & Import Records"}
          </Button>
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Filtering Rules</AlertTitle>
        <AlertDescription className="text-xs space-y-1">
          <p>• <strong>Family transfers</strong>: Same last name on both sides → filtered out</p>
          <p>• <strong>Business-to-business</strong>: Both parties are entities (LLC, Inc, Trust, etc.) → filtered out</p>
          <p>• <strong>Bank transactions</strong>: Any party is a bank/mortgage company → filtered out</p>
          <p>• <strong>Individual buying from business</strong>: Kept (potential owner-finance deal)</p>
          <p>• <strong>Inactive contracts</strong>: DIS LC, DISCHARGE, RELEASE doc types → skipped</p>
          <p>• <strong>Duplicates</strong>: Same document number → only first occurrence kept</p>
        </AlertDescription>
      </Alert>
    </div>
  );
}
